<template>
  <div class="edit-profile">
    <v-card :class="`card-container ${darkMode ? 'dark-border' : 'light-border'}`" flat>
      <div class="headline-container">
        <h2>{{ $t('profileSettings.editProfile') }}</h2>
      </div>

      <SelectProfileImageDialog
        :open="dialogState"
        @imageSelected="imageSelected"
        @cancelled="dialogState = false"
      />

      <v-form ref="form" v-model="valid" @submit.prevent="update">
        <div class="custom-flex custom-wrap">
          <v-avatar class="image" size="130px">
            <div class="img-overlay" @click="dialogState = true">
              <div class="change-image">{{ $t('profileSettings.changeImage') }}</div>
            </div>
            <img
              v-if="me && me.profileImage && me.profileImage.id"
              :src="
                newImageSelected && newImage
                  ? `${newImage.imageURL}?w=200&h=200&fit=crop`
                  : `${me.profileImage.imageURL}?w=200&h=200&fit=crop`
              "
              alt="Profile Image"
            />
          </v-avatar>
          <v-text-field
            v-model="me.displayName"
            class="display-name required"
            name="displayName"
            :label="$t('profileSettings.displayName')"
            :rules="[
              (v) => !!v || 'Cannot be blank',
              (v) => (v && v.length <= 24) || 'Max 24 characters',
              checkDisplayName,
            ]"
            counter="24"
            required
          ></v-text-field>
        </div>
        <div class="custom-flex">
          <v-text-field
            v-model="me.givenName"
            class="given-name left-input-space"
            name="givenName"
            clearable
            :label="$t('profileSettings.firstName')"
          ></v-text-field>
          <v-text-field
            v-model="me.familyName"
            class="family-name right-input-space"
            name="familyName"
            clearable
            :label="$t('profileSettings.lastName')"
          ></v-text-field>
        </div>
        <div class="custom-flex">
          <v-text-field
            v-model="me.username"
            class="username-alt required"
            name="username"
            counter="64"
            clearable
            required
            :label="$t('profileSettings.username')"
            data-lpignore="true"
            autocomplete="off"
            :rules="[
              (v) => (v && v.length >= 0) || '',
              (v) => (v && v.length <= 64) || 'Max 64 characters',
              checkUsername,
            ]"
          ></v-text-field>
        </div>
        <div class="custom-flex">
          <v-select
            v-model="me.handedness"
            class="left-input-space required"
            :items="$options.handOptions"
            :label="$t('profileSettings.dexterity')"
            item-value="value"
            required
          ></v-select>
          <v-select
            v-model="me.gender"
            class="right-input-space"
            :items="$options.genderOptions"
            :label="$t('profileSettings.gender')"
            item-value="value"
            data-lpignore="true"
          ></v-select>
          <v-text-field
            v-model.number="me.height_cm"
            :label="`${$t('profileSettings.height')} (${$t('profileSettings.cm')})`"
            name="height"
            type="number"
            class="right-input-space"
          ></v-text-field>
          <!-- <v-text-field
            v-model="me.phoneNumber"
            class="right-input-space"
            name="phoneNumber"
            :label="$t('profileSettings.phoneNumber')"
            hint="(123) 456-7890"
            v-mask="'(###) ###-####'"
          ></v-text-field> -->
        </div>
        <v-autocomplete
          v-model="userCountry"
          class="required"
          item-value="alpha3"
          item-text="name"
          required
          :label="$t('profileSettings.country')"
          :items="countries"
          :rules="[(v) => !!v || 'Country is required']"
        >
          <template slot="item" slot-scope="data">
            <v-list-item-avatar size="initial" tile>
              <img :src="`${data.item.flagURL}?w=40&h=40`" />
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>{{ data.item.name }}</v-list-item-title>
            </v-list-item-content>
          </template>
        </v-autocomplete>
        <v-textarea
          v-model="me.bio"
          name="bio"
          :label="$t('profileSettings.bio')"
          max="1000"
          counter="1000"
          multi-line
          hint="Bio must be less than 1000 characters"
        ></v-textarea>

        <!-- Disabling temporarily until preferences sorted out -->
        <div v-if="false && me && me.preferences" class="player-prefs mt-4 mb-6">
          <!-- <div class="player-prefs mt-4 mb-6" v-if="me && me.preferences"> -->
          <v-flex xs12>
            <h4>{{ $t('profileSettings.simPrefHeading') }}</h4>
          </v-flex>

          <v-divider class="mb-6"></v-divider>

          <v-expansion-panels>
            <v-expansion-panel>
              <v-expansion-panel-header>
                {{ $t('profileSettings.profileHeading') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Tee Box</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-select
                      v-model="me.preferences.teeColor"
                      label="Tee Color"
                      :items="teeOptions"
                      :item-text="(item) => `${item}`"
                      hide-details
                    ></v-select>
                  </v-list-item-action>
                </v-list-item>
              </v-expansion-panel-content>
              <v-expansion-panel-content>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Skill Level</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-select
                      v-model="me.preferences.skillLevel"
                      label="Skill Level"
                      :items="skillOptions"
                      :item-text="(item) => `${item}`"
                      hide-details
                    ></v-select>
                  </v-list-item-action>
                </v-list-item>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <v-expansion-panel-header>
                {{ $t('profileSettings.gamePlayHeading') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Main View Tracer</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-select
                      v-model="me.preferences.mainViewTracer"
                      label="Tracer Options"
                      :items="mainViewTracerOptions"
                      :item-text="(item) => `${item}`"
                      hide-details
                    ></v-select>
                  </v-list-item-action>
                </v-list-item>
              </v-expansion-panel-content>
              <v-expansion-panel-content>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Top View Tracer</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-select
                      v-model="me.preferences.topViewTracer"
                      label="Tracer Options"
                      :items="topViewTracerOptions"
                      :item-text="(item) => `${item}`"
                      hide-details
                    ></v-select>
                  </v-list-item-action>
                </v-list-item>
              </v-expansion-panel-content>
              <!-- <v-expansion-panel-content>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Camera Options</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-select
                      v-model="me.preferences.cameraType"
                      :items="cameraTypeOptions"
                      label="Camera Types"
                      :item-text="(item) => `${item}`"
                    ></v-select>
                  </v-list-item-action>
                </v-list-item>
              </v-expansion-panel-content> -->
              <v-expansion-panel-content>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Ball Scaling</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-select
                      v-model="me.preferences.ballScaling"
                      label="Options"
                      :items="ballScalingOptions"
                      :item-text="(item) => `${item}`"
                      hide-details
                    ></v-select>
                  </v-list-item-action>
                </v-list-item>
              </v-expansion-panel-content>
              <!-- <v-expansion-panel-content>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Putting Line</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-select
                      v-model="me.preferences.puttingLine"
                      :items="puttingLineOptions"
                      label="Putting Options"
                      :item-text="(item) => `${item}`"
                    ></v-select>
                  </v-list-item-action>
                </v-list-item>
              </v-expansion-panel-content> -->
              <v-expansion-panel-content>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Terrain Grid</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-select
                      v-model="me.preferences.terrainGrid"
                      label="Grid Options"
                      :items="terrainGridOptions"
                      :item-text="(item) => `${item}`"
                      hide-details
                    ></v-select>
                  </v-list-item-action>
                </v-list-item>
              </v-expansion-panel-content>
              <v-expansion-panel-content>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Ball Indicator</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-select
                      v-model="me.preferences.ballIndicator"
                      label="Ball Indicator"
                      :items="ballIndicatorOptions"
                      :item-text="(item) => `${item}`"
                      hide-details
                    ></v-select>
                  </v-list-item-action>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Ball Indicator Distance</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-select
                      v-model="me.preferences.ballIndicatorDistance"
                      label="Distance Options"
                      :items="ballIndicatorDistanceOptions"
                      :item-text="(item) => `${item}`"
                      hide-details
                    ></v-select>
                  </v-list-item-action>
                </v-list-item>
              </v-expansion-panel-content>
              <v-expansion-panel-content>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Pin Indicator</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-select
                      v-model="me.preferences.pinIndicator"
                      label="Options"
                      :items="pinIndicatorOptions"
                      :item-text="(item) => `${item}`"
                      hide-details
                    ></v-select>
                  </v-list-item-action>
                </v-list-item>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <v-expansion-panel-header>
                {{ $t('profileSettings.qamHeading') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Enable Quick Access Menu</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-switch v-model="me.preferences.enableQAM" color="primary"></v-switch>
                  </v-list-item-action>
                </v-list-item>
              </v-expansion-panel-content>
              <v-expansion-panel-content>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Show Mulligan</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-switch v-model="me.preferences.showQAMMulligan" color="primary"></v-switch>
                  </v-list-item-action>
                </v-list-item>
              </v-expansion-panel-content>
              <v-expansion-panel-content>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Show Switch Camera</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-switch v-model="me.preferences.showQAMSwitchCam" color="primary"></v-switch>
                  </v-list-item-action>
                </v-list-item>
              </v-expansion-panel-content>
              <v-expansion-panel-content>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Show Change Sky</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-switch v-model="me.preferences.showQAMChangeSky" color="primary"></v-switch>
                  </v-list-item-action>
                </v-list-item>
              </v-expansion-panel-content>
              <v-expansion-panel-content>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Show Flyby</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-switch v-model="me.preferences.showQAMFlyby" color="primary"></v-switch>
                  </v-list-item-action>
                </v-list-item>
              </v-expansion-panel-content>
              <v-expansion-panel-content>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Show Grid</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-switch v-model="me.preferences.showQAMGrid" color="primary"></v-switch>
                  </v-list-item-action>
                </v-list-item>
              </v-expansion-panel-content>
              <v-expansion-panel-content>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Show Putting Line</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-switch
                      v-model="me.preferences.showQAMPuttingLine"
                      color="primary"
                    ></v-switch>
                  </v-list-item-action>
                </v-list-item>
              </v-expansion-panel-content>
              <v-expansion-panel-content>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Show Simulated Ball Trail</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-switch
                      v-model="me.preferences.showQAMSimBallTrail"
                      color="primary"
                    ></v-switch>
                  </v-list-item-action>
                </v-list-item>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
        <!-- END E6 Preferences section -->

        <div class="additional-information mt-4 mb-6">
          <v-flex xs12>
            <h2>{{ $t('profileSettings.addInfoHeading') }}</h2>
          </v-flex>

          <v-divider class="mb-6"></v-divider>

          <v-expansion-panels :value="0" class="">
            <v-expansion-panel class="pb-6">
              <v-expansion-panel-header class="flex">
                <v-row wrap class="px-2">
                  <v-flex xs12>
                    <v-tooltip top>
                      <template #activator="{ on, attrs }">
                        <v-icon color="primary" dark v-bind="attrs" v-on="on"> info </v-icon>
                      </template>
                      <span><em>Coming Soon!</em></span>
                    </v-tooltip>
                    <b class="pl-2">Enhanced Profile Questionaire</b>
                    <small><em> (optional)</em></small>
                  </v-flex>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="custom-flex pb-4">
                  <v-alert border="top" colored-border color="primary" elevation="2">
                    Entering in the following information for your account will allow for additional
                    account specific features based on your responses.
                  </v-alert>
                </div>
                <v-row wrap>
                  <v-col cols="12" sm="12" md="6">
                    <h4>What is your profession?</h4>
                    <v-text-field
                      ref="profession"
                      v-model="formValues.profession"
                      :placeholder="`What do you do?`"
                      name="profession"
                      tabindex="1"
                      maxlength="20"
                      hide-details="auto"
                      type="text"
                      clearable
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="12" md="6">
                    <h4>What is your golf experience level?</h4>
                    <v-select
                      v-model="formValues.experienceLevel"
                      :items="golferOptions"
                      :placeholder="`Choose your experience level`"
                      data-cy="experienceLevel"
                      tabindex="1"
                      hide-details="auto"
                      clearable
                    ></v-select>
                  </v-col>
                  <v-col cols="12" sm="12" md="6">
                    <h4>What is your golf handicap?</h4>
                    <v-text-field
                      ref="handicap"
                      v-model.number="formValues.handicap"
                      name="handicap"
                      :placeholder="`What is your handicap?`"
                      tabindex="1"
                      hide-details="auto"
                      type="number"
                      inputmode="numeric"
                      pattern="[0-9]*"
                      clearable
                      @focus="$event.target.select()"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="12" md="6">
                    <h4>What is your shot tendency?</h4>
                    <v-select
                      v-model="formValues.shotTendency"
                      :items="shotTypes"
                      :placeholder="`Shot Tendency`"
                      data-cy="shotTendency"
                      tabindex="1"
                      hide-details="auto"
                      clearable
                    ></v-select>
                  </v-col>
                  <v-col cols="12" sm="12" md="6">
                    <h4>Approximately how many years have you been playing golf?</h4>
                    <v-text-field
                      ref="yearsPlayed"
                      v-model.number="formValues.yearsPlayed"
                      name="yearsPlayed"
                      :placeholder="`How many years played?`"
                      tabindex="1"
                      hide-details="auto"
                      type="number"
                      inputmode="numeric"
                      pattern="[0-9]*"
                      clearable
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="12" md="6">
                    <h4>How many times per year on average do you play?</h4>
                    <v-text-field
                      ref="avgTimesPlayed"
                      v-model.number="formValues.averageTimesPlayedPerYear"
                      name="averageTimesPlayedPerYear"
                      :placeholder="`How many times played?`"
                      tabindex="1"
                      hide-details="auto"
                      type="number"
                      inputmode="numeric"
                      pattern="[0-9]*"
                      clearable
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="12" md="6">
                    <h4>What is your favorite sport?</h4>
                    <v-select
                      v-model="formValues.favoriteSport"
                      :items="sportOptions"
                      class=""
                      name="favoriteSport"
                      :placeholder="`Favorite Sport?`"
                      tabindex="1"
                      hide-details="auto"
                      clearable
                    ></v-select>
                  </v-col>
                  <v-col cols="12" sm="12" md="6">
                    <h4>Who is your favorite pro golfer?</h4>
                    <v-select
                      ref="favoriteGolfer"
                      v-model="formValues.favoriteGolfer"
                      :items="golferList"
                      data-cy="favoriteGolfer"
                      name="favoriteGolfer"
                      :placeholder="`Favorite Golfer?`"
                      tabindex="1"
                      hide-details="auto"
                      clearable
                    ></v-select>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <v-expansion-panels :value="0" class="mt-4">
            <v-expansion-panel>
              <v-expansion-panel-header class="flex">
                <v-row wrap class="px-2">
                  <v-flex xs12>
                    <v-tooltip top>
                      <template #activator="{ on, attrs }">
                        <v-icon color="info" dark v-bind="attrs" v-on="on"> info </v-icon>
                      </template>
                      <span> Statistics Enhancements <em>Coming Soon!</em> </span>
                    </v-tooltip>
                    <b class="pl-2">Additional Profile Information for Enhanced Stats</b>
                    <small><em> (optional)</em></small>
                  </v-flex>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="custom-flex pb-4">
                  <v-alert border="top" colored-border color="info" elevation="2">
                    This information will allow E6 Connect and APEX to create enhanced user
                    statistics and metrics for you based on your demographic information.
                  </v-alert>
                </div>
                <div class="custom-flex">
                  <v-text-field
                    v-model="me.city"
                    class="right-input-space"
                    name="city"
                    label="City"
                    hint="Salt Lake City"
                  ></v-text-field>

                  <!-- Show state select if USA country, otherwise show text input -->
                  <v-select
                    v-if="me && me.countryAlpha3 === 'USA'"
                    v-model="me.state_province"
                    label="State/Province"
                    item-value="value"
                    item-title="text"
                    class="right-input-space"
                    data-cy="state"
                    tabindex="1"
                    :placeholder="`Utah`"
                    :items="$options.stateList"
                    hide-details="auto"
                    clearable
                  ></v-select>
                  <v-text-field
                    v-else
                    v-model="me.state_province"
                    class="right-input-space"
                    name="state"
                    label="State/Province"
                    hint="Utah"
                  ></v-text-field>
                </div>

                <div class="custom-flex">
                  <v-text-field
                    v-model="me.zip"
                    class="right-input-space"
                    name="zip"
                    label="Zip/Postal Code"
                  ></v-text-field>
                  <v-menu v-model="birthdateMenu" :close-on-content-click="false" max-width="290">
                    <template #activator="{ on, attrs }">
                      <v-text-field
                        :value="computedDateFormattedDatefns"
                        clearable
                        label="Date of Birth"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        @click:clear="birthdate = null"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="birthdate"
                      @change="birthdateMenu = false"
                    ></v-date-picker>
                  </v-menu>
                  <v-text-field
                    v-model.number="me.real_ghin"
                    class="right-input-space"
                    name="ghin"
                    label="Real GHIN"
                    hint="This is your real world Golf Handicap Index value."
                  ></v-text-field>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>

        <v-btn type="submit" :disabled="!valid" class="primary">Update profile</v-btn>
        <v-btn :to="`/users/${me.id}`" text>View Profile</v-btn>
      </v-form>
    </v-card>

    <SettingsBottomNav :limited-user="limitedUser" />
  </div>
</template>

<script>
import { format, parseISO } from 'date-fns';
import countryMixin from '@/mixins/countryList';
import meMixin from '@/mixins/meMixin';
import utilityMixin from '@/mixins/utilityMixin';
import SettingsBottomNav from '@/components/SettingsBottomNav';
import validateDisplayName from '@/lib/displayNameValidator.js';
import validateUserName from '@/lib/usernameValidator.js';
import { genderOptions, handOptions, stateList } from '@/lib/constants';
// import { isDev } from '@/utils/helpers';

//* Code-splitting component imports
const SelectProfileImageDialog = () => {
  return import(/* webpackPrefetch: true */ '@/components/SelectProfileImageDialog');
};

export default {
  name: 'EditProfile',

  components: {
    SettingsBottomNav,
    SelectProfileImageDialog,
  },
  mixins: [countryMixin, meMixin, utilityMixin],

  props: {
    limitedUser: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      valid: true,
      dialogState: false,
      newImageSelected: false,
      newImage: '',

      birthdate: format(parseISO(new Date().toJSON()), 'yyyy-MM-dd'),
      birthdateMenu: false,
      preferences: {},

      teeOptions: ['Black', 'Blue', 'White', 'Red', 'Yellow'],
      skillOptions: ['Beginner', 'Advanced'],

      mainViewTracerOptions: ['Comet', 'Solid', 'Echo', 'Off'],
      topViewTracerOptions: ['Solid', 'Off'],
      cameraTypeOptions: [],
      ballScalingOptions: ['Small', 'Medium', 'Large'],
      puttingLineOptions: ['Off', 'Play Break', 'Align Break'],
      terrainGridOptions: ['On', 'Off'],
      ballIndicatorOptions: ['Auto', 'Always On', 'Off'],
      ballIndicatorDistanceOptions: ['Total Distance', 'Distance To Pin'],
      pinIndicatorOptions: ['Always On', 'Auto', 'Off'],

      formValues: {
        profession: '',
        favoriteGolfer: '',
        favoriteSport: '',
        experienceLevel: null,
        yearsPlayed: null,
        averageTimesPlayedPerYear: null,
        shotTendency: null,
        handicap: null,
      },
      sportOptions: [
        'Baseball',
        'Basketball',
        'Cricket',
        'Golf',
        'Cycling',
        'Extreme Juggling',
        'Football (American)',
        'Football (Soccer)',
        'Formula One',
        'Ice Hockey',
        'Lucha libre wrestling',
        'Mixed Martial Arts',
        'Ostrich Racing',
        'Rugby',
        'Skiing',
        'Sheep Counting',
        'Pickleball',
        'Quidditch',
        'Rodeo Clowning',
        'Tennis',
        'Underwater Hockey',
        'Volleyball',
      ],
      golferList: [
        'Myself',
        'Arnold Palmer',
        'Carl Spackler (Greenskeeper from Caddyshack)',
        'Annika Sörenstam',
        'Dustin Johnson',
        'Happy Gilmore',
        'Justin Rose',
        'Jack Nicklaus',
        'Bryson DeChambeau',
        'John Rahm',
        'Xander Schauffele',
        'Nelly Korda',
        'Jordan Spieth',
        'Sir Nick Faldo',
        'Ludvig Åberg',
        'Phil Mickelson',
        'Rickie Fowler',
        'Rory McIlroy',
        'Scottie Scheffler',
        'Shooter McGavin',
        'Seve Ballesteros',
        'Hideki Matsuyama',
        'Roy McAvoy (Tin Cup)',
        'Tiger Woods',
        'The Bushwood Gopher (Caddyshack)',
        'Collin Morikawa',
      ],
      golferOptions: ['Never Played', 'Beginner', 'Intermediate', 'Expert'],
      shotTypes: ['Fade', 'Draw', 'Slice', 'Hook', 'All over the place', 'Who knows'],
    };
  },

  computed: {
    computedDateFormattedDatefns() {
      return this.birthdate ? format(parseISO(this.birthdate), 'MM/dd/yyyy') : '';
    },

    // formattedPhoneNumber: {
    //   get() {
    //     if (this.me?.phoneNumber) {
    //       return this.me?.phoneNumber?.replace('+', '');
    //     }
    //   },
    //   set(newValue) {
    //     return newValue;
    //   },
    // },

    userCountry: {
      get() {
        if (this.countryList) {
          const countryLength = this.countryList?.length ?? 0;
          for (let idx = 0; idx < countryLength; ++idx) {
            if (
              (this.countryList?.[idx]?.alpha3 &&
                this.countryList?.[idx]?.alpha3 === this.me?.countryAlpha3) ||
              (this.countryList?.[idx]?.alpha2 &&
                this.countryList?.[idx]?.alpha2 === this.me?.countryAlpha3)
            ) {
              return this.countryList[idx];
            }
          }
        }
        return {};
      },
      set(newValue) {
        this.me.countryAlpha3 = newValue;
      },
    },
  },

  watch: {
    getMe(newValue) {
      if (newValue?.birthdate) {
        const [date] = newValue?.birthdate?.split('T') ?? [];
        this.birthdate = format(parseISO(date), 'yyyy-MM-dd');
      }
    },
  },

  //* example user preference options
  // preferences: {
  //   "avatarEnabled": "Off",
  //   "ballIndicator": "Auto",
  //   "ballIndicatorDistance": "Total Distance",
  //   "ballScaling": "Small",
  //   "cameraType": "Follow Mode",
  //   "darkMode": false,
  //   "enableQAM": true,
  //   "handicap": 0,
  //   "language": "English",
  //   "mainViewTracer": "Comet",
  //   "pinIndicator": "Always On",
  //   "playerColor": "FFBA2127",
  //   "puttingArrow": "Off",
  //   "puttingArrowFade": "Off",
  //   "quickLogin": "Off",
  //   "rememberOnSim": "Off",
  //   "showQAMChangeSky": true,
  //   "showQAMFlyAimMarkerPinToCamera": true,
  //   "showQAMFlyby": true,
  //   "showQAMGrid": true,
  //   "showQAMInRoundPractice": true,
  //   "showQAMMinimizeHUD": true,
  //   "showQAMMulligan": true,
  //   "showQAMPuttingLine": true,
  //   "showQAMSimBallTrail": true,
  //   "showQAMSwitchCam": true,
  //   "showQAMZoomToAimMarker": true,
  //   "skillLevel": "Beginner",
  //   "slowSwing": "Off",
  //   "teeColor": "Blue",
  //   "teeHeight": "Medium",
  //   "terrainGrid": "On",
  //   "topViewTracer": "Solid"
  // }

  created() {
    //* non-reactive constants
    this.$options.genderOptions = genderOptions;
    this.$options.handOptions = handOptions;
    this.$options.stateList = stateList;
  },

  mounted() {
    const hasPrefs =
      (this.me?.preferences && 'darkMode' in this.me?.preferences) ||
      (this.getMe?.preferences && 'darkMode' in this.getMe?.preferences);

    if (!hasPrefs) {
      this.$store.dispatch('users/me').then(() => {
        this.checkIntroData();
      });
    }
    if (this.getMe?.birthdate) {
      const [date] = this.getMe?.birthdate?.split('T') ?? [];
      this.birthdate = format(parseISO(date), 'yyyy-MM-dd');
    }
    if (this.getMe?.id) {
      // const { handicap, handicapSet } = this.getMe;
      this.checkIntroData();
    }
  },

  methods: {
    checkDisplayName() {
      return validateDisplayName(this.me.displayName);
    },

    checkUsername() {
      return validateUserName(this.me.username);
    },

    update() {
      if (this.newImageSelected) {
        this.me.profileImageID = this.newImage.id;
      }

      // Deep clone the current user object to avoid mutation issues
      const payload = JSON.parse(JSON.stringify(this.me));

      // Update top-level fields
      payload.countryAlpha3 = this.userCountry.alpha3;
      payload.birthdate = this.computedDateFormattedDatefns;
      payload.height_cm = Math.round(payload?.height_cm) ?? 0;

      // Properly merge preferences
      payload.preferences = {
        ...(payload?.preferences || {}),
        ...(this.getMe?.preferences || {}),
        ...this.preferences,
      };

      // old - remove
      // const payload = {
      //   ...this.me,
      //   countryAlpha3: this.userCountry.alpha3,
      //   birthdate: this.computedDateFormattedDatefns,
      //   height_cm: Math.round(this.me?.height_cm) ?? 0,
      //   preferences: { ...this.me?.preferences, ...this.getMe.preferences, ...this.preferences },
      // };

      //* Don't set birthdate to today by default
      if (payload?.birthdate === format(new Date(), 'MM/dd/yyyy')) {
        delete payload.birthdate;
      }

      //* remove permissions here as well
      delete payload.permissions;

      //* Ensure apex_preferences exists
      if (!payload.apex_preferences) {
        payload.apex_preferences = {};
      }

      // Create a proper copy of the existing intro_data
      const currentIntroData = payload?.intro_data || {};

      // Update intro_data with new values
      payload.intro_data = {
        ...currentIntroData,
        ...(this.me?.id && { id: this.me?.id }),
        ...(this.userCountry?.name && { country: this.userCountry?.name }),
        ...(this.me?.handedness && { handedness: this.me?.handedness }),
        ...(this.me?.displayName && { displayName: this.me?.displayName }),
        ...(this.me?.state_province && { state_province: this.me?.state_province }),
        ...(this.formValues?.profession && { profession: this.formValues?.profession }),
        ...(this.formValues?.favoriteGolfer && { favoriteGolfer: this.formValues?.favoriteGolfer }),
        ...(this.formValues?.favoriteSport && { favoriteSport: this.formValues?.favoriteSport }),
        ...(this.formValues?.experienceLevel && {
          experienceLevel: this.formValues?.experienceLevel,
        }),
        ...(this.formValues?.yearsPlayed && { yearsPlayed: this.formValues?.yearsPlayed }),
        ...(this.formValues?.averageTimesPlayedPerYear !== undefined && {
          averageTimesPlayedPerYear: this.formValues?.averageTimesPlayedPerYear,
        }),
        ...(this.formValues?.shotTendency && { shotTendency: this.formValues?.shotTendency }),
        ...((this.formValues?.handicap || this.formValues?.handicap === 0) && {
          handicap: this.formValues?.handicap,
        }),
      };

      // old - remove
      // //* Set up intro data in apex preferences if they exist
      // // const hasQuestionFormValues = this.hasQuestionFormValues(this.formValues);
      // payload.apex_preferences = {
      //   ...payload.apex_preferences,
      //   intro_data: {
      //     ...payload?.apex_preferences?.intro_data,
      //     ...(this.me?.id && { id: this.me?.id }),
      //     ...(this.userCountry?.name && { country: this.userCountry?.name }),
      //     ...(this.me?.handedness && { handedness: this.me?.handedness }),
      //     ...(this.me?.displayName && { displayName: this.me?.displayName }),
      //     ...(this.me?.state_province && { state_province: this.me?.state_province }),
      //     ...(this.formValues?.profession && { profession: this.formValues?.profession }),
      //     ...(this.formValues?.favoriteGolfer && {
      //       favoriteGolfer: this.formValues?.favoriteGolfer,
      //     }),
      //     ...(this.formValues?.favoriteSport && {
      //       favoriteSport: this.formValues?.favoriteSport,
      //     }),
      //     ...(this.formValues?.experienceLevel && {
      //       experienceLevel: this.formValues?.experienceLevel,
      //     }),
      //     ...(this.formValues?.yearsPlayed && { yearsPlayed: this.formValues?.yearsPlayed }),
      //     ...(this.formValues?.averageTimesPlayedPerYear && {
      //       averageTimesPlayedPerYear: this.formValues?.averageTimesPlayedPerYear,
      //     }),
      //     ...(this.formValues?.shotTendency && { shotTendency: this.formValues?.shotTendency }),
      //     ...((this.formValues?.handicap || this.formValues?.handicap === 0) && {
      //       handicap: this.formValues?.handicap,
      //     }),
      //   },
      // };

      // console.log('update payload:', payload);

      this.$store
        .dispatch('users/update', payload)
        .then(() => {
          // Set handicap if exists
          if (this.formValues?.handicap || this.formValues?.handicap === 0) {
            const hcpPayload = { handicap: parseFloat(this.formValues?.handicap?.toFixed(2)) };
            this.updateHCP(hcpPayload);
          }

          this.$store.dispatch('users/me', payload);

          this.$root.vtoast.show({
            text: `Your profile has been updated`,
            color: 'success',
          });
        })
        .catch((err) => {
          const notUniqueRes = `duplicate key value violates unique constraint "users_with_deleted_username_key"`;
          const { detail = '' } = err?.data;
          if (detail?.includes(notUniqueRes)) {
            `${this.$t('general.error')}`;
            this.$root.vtoast.show({
              text: `${this.$t('general.error')}: ${this.$t(
                'profileSettings.usernameNotUniqueMsg'
              )}`,
              color: `error`,
            });
          } else {
            this.$root.vtoast.show({
              text: err?.data?.detail ?? 'Error updating profile',
              color: `error`,
            });
          }
        });
    },

    imageSelected(profileImage) {
      this.dialogState = false;
      this.newImageSelected = true;
      this.newImage = profileImage;
    },

    hasQuestionFormValues(obj) {
      // Iterate over all properties in the object
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          // Check if the property is not null, undefined, or an empty string
          if (obj[key] !== null && obj[key] !== undefined && obj[key] !== '') {
            return true;
          }
        }
      }
      return false;
    },

    checkIntroData() {
      const { intro_data = {} } = this.getMe;
      if (this.utility_isObject(intro_data)) {
        this.formValues = {
          ...this.formValues,
          ...intro_data,
          handicap: this.getMe?.user_set_handicap || intro_data?.handicap || null,
        };
      }
    },

    // Set user's handicap based on formValue entered
    updateHCP(payload) {
      let updatedPayload = {};
      const { handicap, handicapSet, id } = this.me;

      //* if handicap and handicapSet - only pass new user handicap in payload
      //* if !handicap and handicap === null and handicapSet === false - pass handicapSet, handicap and new user handicap in payload
      if (handicap === null && !handicapSet) {
        updatedPayload = {
          userID: id,
          handicapSet: true,
          handicap: payload.handicap,
          user_set_handicap: payload.handicap,
        };
      } else {
        updatedPayload = {
          userID: id,
          user_set_handicap: payload.handicap,
        };
      }

      this.$store
        .dispatch('users/update', updatedPayload)
        .then((res) => {
          // console.log('res:', res);
        })
        .catch((err) => {
          console.error('err:', err);
        });
    },
  },
};
</script>

<style scoped>
.edit-profile {
  margin-top: -65px;
  min-height: calc(60vh - 65px) !important;
}

.edit-profile .card-container {
  padding: 30px !important;
  border-radius: 10px;
  border-radius: var(--border-10);
}

.custom-flex {
  display: flex;
}

.custom-wrap {
  flex-wrap: wrap;
}

.headline-container {
  margin-bottom: 24px;
}

.left-input-space {
  margin-right: 5px;
}

.right-input-space {
  margin-left: 5px;
}

.display-name,
.username {
  margin-left: 24px;
  margin-top: 72px;
}

.image {
  position: relative;
}

.img-overlay {
  top: -1px;
  left: -1px;
  width: 102%;
  height: 102%;
  border-radius: 50%;
  position: absolute;
  background-color: rgba(0, 0, 0, 0.8);
  opacity: 0;
  transition: opacity 0.5s;
  display: flex;
  justify-content: center;
  flex-direction: column;
  cursor: pointer;
  z-index: 99;
}

.img-overlay:hover {
  opacity: 1;
}

.change-image {
  color: #fff;
  font-weight: 500;
}

* >>> .v-expansion-panel-content__wrap {
  padding-bottom: 0;
}
* >>> .v-list-item__action {
  max-width: 195px;
}

@media (max-width: 600px) {
  .edit-profile {
    margin-top: 0px;
  }
}
</style>
